import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Plus, ChevronLeft, Loader2, CheckCircle, UploadCloud, X, Save, Trash2, Layers, Settings } from 'lucide-react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import toast from 'react-hot-toast';

const AddProduct = () => {
  const { id } = useParams(); 
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({ 
    name: '', 
    price: '', 
    category: '', 
    images: [], 
    description: '',
    variants: [] 
  });
  
  const [hasVariants, setHasVariants] = useState(false);
  const [categories, setCategories] = useState([]); // Naya state categories ke liye
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const CLOUD_NAME = "dm06twdcj"; 
  const UPLOAD_PRESET = "whatsappCatelog"; 

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Nayi Categories API se data uthayen
        const catRes = await api.get('/categories');
        setCategories(catRes.data);

        // 2. Agar Edit Mode hai to product data uthayen
        if (isEditMode) {
          const res = await api.get(`/products/single/${id}`);
          setFormData(res.data);
          if (res.data.variants && res.data.variants.length > 0) {
            setHasVariants(true);
          }
        }
        setInitialLoading(false);
      } catch (err) { 
        toast.error("Failed to load data!");
        setInitialLoading(false);
      }
    };
    fetchData();
  }, [id, isEditMode]);

  // --- VARIANT HELPERS ---
  const addVariant = () => {
    setFormData({ ...formData, variants: [...formData.variants, { name: '', price: '' }] });
  };

  const removeVariant = (index) => {
    const newVariants = formData.variants.filter((_, i) => i !== index);
    setFormData({ ...formData, variants: newVariants });
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...formData.variants];
    newVariants[index][field] = value;
    setFormData({ ...formData, variants: newVariants });
  };

  // --- IMAGE UPLOAD LOGIC ---
const handleMultipleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImage(true);
    const toastId = toast.loading(`Uploading ${files.length} photos...`);
    
    let uploadedUrls = [...formData.images];
    try {
      for (const file of files) {
        const data = new FormData();
        data.append('file', file);
        data.append('upload_preset', UPLOAD_PRESET);
        const res = await axios.post(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, data);
        
        // --- OPTIMIZATION START ---
        // secure_url: https://res.cloudinary.com/.../upload/v1234/abc.png
        // f_auto: Format automatically WebP/Avif banata hai
        // q_auto: Quality optimize karta hai bina visible farq ke
        // w_800: Image ko 800px width par resize kar deta hai (Best for Mobile)
        const optimizedUrl = res.data.secure_url.replace('/upload/', '/upload/f_auto,q_auto,w_800/');
        // --- OPTIMIZATION END ---

        uploadedUrls.push(optimizedUrl);
      }
      setFormData({ ...formData, images: uploadedUrls });
      toast.success("Photos uploaded successfully!", { id: toastId });
    } catch (err) { 
      toast.error("Error uploading images", { id: toastId }); 
    }
    finally { setUploadingImage(false); }
  };

  const removeImage = (index) => {
    setFormData({ ...formData, images: formData.images.filter((_, i) => i !== index) });
    toast.success("Photo removed");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.images.length === 0) return toast.error("Please add at least one photo!");
    if (!formData.category) return toast.error("Please select a category!");
    
    if (hasVariants && (formData.variants.length === 0 || formData.variants.some(v => !v.name || !v.price))) {
      return toast.error("Please fill all variant details.");
    }

    setLoading(true);
    const actionToast = toast.loading(isEditMode ? "Updating product..." : "Adding product...");

    const finalData = {
      ...formData,
      variants: hasVariants ? formData.variants : []
    };

    try {
      if (isEditMode) {
        await api.put(`/products/update/${id}`, finalData);
        toast.success('Product updated successfully! ✨', { id: actionToast });
      } else {
        await api.post('/products/add', finalData);
        toast.success('Product listed successfully! 🚀', { id: actionToast });
      }
      navigate('/dashboard/my-products');
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Something went wrong', { id: actionToast });
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) return (
    <div className="flex flex-col items-center justify-center h-64">
      <Loader2 className="animate-spin text-green-600" size={40} />
      <p className="mt-4 text-gray-500 font-bold">Loading setup...</p>
    </div>
  );

  return (
    <div className="pb-10 max-w-4xl mx-auto px-4 sm:px-0">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-black text-gray-900 tracking-tight">
            {isEditMode ? 'Edit Product' : 'Add New Item'}
          </h2>
          <p className="text-gray-500 font-medium italic">
             {formData.name || 'Set your product details'}
          </p>
        </div>
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-gray-400 font-bold hover:text-black transition-colors">
          <ChevronLeft size={20}/> Back
        </button>
      </div>
      
      <div className="bg-white p-6 md:p-10 rounded-[2.5rem] shadow-sm border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Gallery */}
          <div className="space-y-3">
            <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Product Gallery</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {formData.images.map((url, index) => (
                <div key={index} className="relative h-32 rounded-2xl overflow-hidden border border-gray-100 group shadow-sm transition-transform hover:scale-[1.02]">
                  <img src={url} alt="preview" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeImage(index)} className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <label className="h-32 border-4 border-dashed border-gray-100 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-green-400 hover:bg-green-50 transition-all bg-gray-50">
                {uploadingImage ? <Loader2 className="animate-spin text-green-600" size={24} /> : (
                  <>
                    <UploadCloud className="text-gray-300" size={32} />
                    <span className="text-[10px] font-bold text-gray-400 mt-1 uppercase">Add Photo</span>
                  </>
                )}
                <input type="file" multiple className="hidden" onChange={handleMultipleImageUpload} accept="image/*" />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Product Name</label>
              <input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full p-4 bg-gray-50 rounded-2xl outline-none border border-transparent focus:border-green-500 focus:bg-white font-bold text-gray-800" placeholder="e.g. Zinger Burger" />
            </div>
            
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Base Price (PKR)</label>
              <input 
                required={!hasVariants} 
                disabled={hasVariants}
                type="number" 
                value={formData.price} 
                onChange={(e) => setFormData({...formData, price: e.target.value})} 
                className={`w-full p-4 rounded-2xl outline-none border border-transparent font-bold transition-all ${hasVariants ? 'bg-gray-100 text-gray-400' : 'bg-gray-50 focus:border-green-500 focus:bg-white text-gray-800'}`} 
                placeholder={hasVariants ? "Variant prices active" : "e.g. 450"} 
              />
            </div>
          </div>

          {/* Category Dropdown (Integrated with Manager) */}
          <div className="space-y-2">
            <div className="flex justify-between items-center px-2">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Category</label>
              <Link to="/dashboard/categories" className="text-[10px] font-black text-green-600 uppercase flex items-center gap-1 hover:underline">
                <Settings size={12} /> Manage List
              </Link>
            </div>
            <select 
              required 
              value={formData.category} 
              onChange={(e) => setFormData({...formData, category: e.target.value})} 
              className="w-full p-4 bg-gray-50 rounded-2xl outline-none appearance-none font-bold text-gray-700 border border-transparent focus:bg-white focus:ring-2 focus:ring-green-500 shadow-sm"
            >
              <option value="">-- Choose Category --</option>
              {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            {categories.length === 0 && (
              <p className="text-[10px] text-red-400 font-bold ml-2">No categories found. Please add some first.</p>
            )}
          </div>

          {/* Variants */}
          <div className="bg-green-50/50 p-6 rounded-[2.5rem] border border-green-100/50">
             <label className="flex items-center justify-between cursor-pointer group">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-xl transition-colors ${hasVariants ? 'bg-green-600 text-white' : 'bg-white text-green-600 border border-green-100'}`}>
                    <Layers size={20} /> 
                  </div>
                  <div> 
                    <p className="text-sm font-black text-gray-900 uppercase">Product Variants</p>
                    <p className="text-[10px] text-gray-500 font-bold italic">Enable for S, M, L or different weights</p>
                  </div>
                </div>
                <input 
                  type="checkbox" 
                  className="w-6 h-6 rounded-lg accent-green-600" 
                  checked={hasVariants} 
                  onChange={(e) => {
                    setHasVariants(e.target.checked);
                    if (e.target.checked && formData.variants.length === 0) addVariant();
                  }} 
                />
             </label>

             {hasVariants && (
               <div className="mt-5 space-y-3">
                 {formData.variants.map((v, index) => (
                   <div key={index} className="flex gap-2 items-center bg-white p-3 rounded-2xl shadow-sm border border-green-50 animate-in fade-in slide-in-from-top-1">
                     <input required placeholder="Size/Type" value={v.name} onChange={(e) => updateVariant(index, 'name', e.target.value)} className="flex-1 p-3 bg-gray-50 rounded-xl text-sm font-bold border-transparent outline-none focus:border-green-400" />
                     <input required type="number" placeholder="Price" value={v.price} onChange={(e) => updateVariant(index, 'price', e.target.value)} className="w-28 p-3 bg-gray-50 rounded-xl text-sm font-bold border-transparent outline-none focus:border-green-400" />
                     <button type="button" onClick={() => removeVariant(index)} className="p-3 text-red-400 hover:text-red-500 transition-colors"><Trash2 size={18} /></button>
                   </div>
                 ))}
                 <button type="button" onClick={addVariant} className="w-full py-3 border-2 border-dashed border-green-200 rounded-2xl text-green-600 font-black text-[10px] uppercase hover:bg-green-100 transition-all flex items-center justify-center gap-2">
                   <Plus size={16} /> Add More
                 </button>
               </div>
             )}
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Description</label>
            <div className="ck-custom-editor border border-gray-100 rounded-[2rem] overflow-hidden">
              <CKEditor
                editor={ClassicEditor}
                data={formData.description}
                onChange={(event, editor) => setFormData({ ...formData, description: editor.getData() })}
                config={{ toolbar: ['bold', 'italic', 'link', 'bulletedList', 'numberedList'] }}
              />
            </div>
          </div>

          <button 
            disabled={loading || uploadingImage} 
            className={`w-full py-5 rounded-[1.5rem] font-black text-xl transition-all shadow-xl flex items-center justify-center gap-3 active:scale-95 ${
              isEditMode ? 'bg-green-600' : 'bg-black'
            } text-white disabled:bg-gray-200`}
          >
            {loading ? <Loader2 className="animate-spin" /> : (isEditMode ? <Save size={24} /> : <CheckCircle size={24} />)}
            {loading ? 'Saving...' : (isEditMode ? 'Update Item' : 'Post Item')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddProduct;